import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback } from 'react';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import {
	type CustomPlacementProps,
	PlacementFrontend,
	type ViewInsightOutcome,
} from '@post-office/browser--jira-tanuki/jira-reports-agent-roi-spotlight';

/**
 * Jira renders the Agent ROI changeboarding tour; Post Office only decides eligibility.
 * This component therefore renders no UI of its own — it invokes the product's render
 * prop and hands it completion callbacks wired into Post Office.
 */
export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<CustomPlacementProps>;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<CustomPlacementProps>(),
		Component: ({ children, dismiss }) => {
			const { messageDismissed } = useMessageEvent();
			const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();

			/**
			 * Order matters: the Post Office calls must be issued before `dismiss()`, so that a
			 * future placement-side implementation that unmounts this subtree cannot tear down
			 * the provider before the requests leave.
			 *
			 *  - `messageDismissed()` writes `dismissedTimestamp`, which `DismissalCheckFilter`
			 *    denies on. This is the once-per-user guarantee. Its `@deprecated` tag points at
			 *    the semantic interaction handlers below, which do not persist anything — do not
			 *    drop this call.
			 *  - `onClosed()`/`onCtaActioned()` are analytics only.
			 *  - `dismiss()` is reserved for session-local suppression. The placement currently
			 *    passes a no-op, so teardown rests on Jira's own tour state.
			 *
			 * Both Post Office calls reject with `MessageLifecycleContextError` if any of
			 * `messageInstanceId`, `messageTemplateId` or `placementId` is missing. Left
			 * unhandled that is an invisible failure, and for `messageDismissed` it silently
			 * breaks the once-per-user guarantee, so both rejections are surfaced.
			 */
			const persistDismissal = useCallback(() => {
				messageDismissed().catch((error: unknown) => {
					// Dismissal was not persisted, so the message may be delivered again.
					// eslint-disable-next-line no-console
					console.error('[jira-reports-agent-roi-spotlight] messageDismissed failed', error);
				});
			}, [messageDismissed]);

			const handleDismiss = useCallback(() => {
				persistDismissal();
				void onClosed({ interactionIdentifier: 'dismiss' });
				dismiss();
			}, [dismiss, onClosed, persistDismissal]);

			const handleViewInsight = useCallback(
				(outcome: ViewInsightOutcome) => {
					persistDismissal();
					void onCtaActioned({
						interactionIdentifier: 'view-insight',
						additionalMetadata: { outcome },
					});
					dismiss();
				},
				[dismiss, onCtaActioned, persistDismissal],
			);

			// `noValidation` means props cross the package boundary unchecked, so fail closed
			// rather than throwing inside the product's render tree.
			return <>{children?.({ dismiss: handleDismiss, viewInsight: handleViewInsight }) ?? null}</>;
		},
	},
});
